import React, { useState, useEffect } from 'react';
import { DatePeriodFilters } from '@/utils/charts';
import ChartDateFilterComponent, {
  CustomDates,
  CustomDatesError,
} from './ChartDateFilter.component';

type ChartDateFilterContainerProps = {
  currentFilter: DatePeriodFilters;
  filters?: DatePeriodFilters[];
  disabledFilters?: DatePeriodFilters[];
  hasCustomDate?: boolean;
  initialCustomDates?: {
    start: string;
    end: string;
  };
  setNewFilter?: (value: DatePeriodFilters) => void;
  setCustomDate?: (startDate?: Date, endDate?: Date) => void;
};

const ChartDateFilterContainer = function ({
  currentFilter,
  filters,
  disabledFilters = [],
  setNewFilter,
  hasCustomDate = false,
  initialCustomDates,
  setCustomDate,
}: ChartDateFilterContainerProps): JSX.Element {
  const [isCustomDateDialogOpen, setIsCustomDateDialogOpen] = useState(false);
  const [customDates, setCustomDates] = useState<CustomDates>(null);
  const [customDatesError, setCustomDatesError] = useState<CustomDatesError>(null);

  useEffect(() => {
    if (initialCustomDates && currentFilter === DatePeriodFilters.CUSTOM_DATE) {
      setCustomDates({
        start: new Date(initialCustomDates.start),
        end: new Date(initialCustomDates.end),
      });
    }
  }, [initialCustomDates, currentFilter]);

  const filterValues =
    filters ?? Object.values(DatePeriodFilters).filter((key) => typeof key === 'number');

  const handleChangeFilter = (datePeriod: DatePeriodFilters): void => {
    if (currentFilter === datePeriod || !setNewFilter) return;
    setNewFilter(datePeriod);
    setCustomDates(null);
  };

  const toggleCustomDateDialog = (): void => {
    setIsCustomDateDialogOpen((currentValue) => !currentValue);
  };

  const setCustomStartDate = (date: Date): void => {
    setCustomDates((currentValue) => ({ ...currentValue, start: date }));

    if (customDatesError?.start) {
      setCustomDatesError((currentValue) => ({ ...currentValue, start: '' }));
    }
  };

  const setCustomEndDate = (date: Date): void => {
    setCustomDates((currentValue) => ({ ...currentValue, end: date }));

    if (customDatesError?.end) {
      setCustomDatesError((currentValue) => ({ ...currentValue, end: '' }));
    }
  };

  const handleSetCustomDates = (period: 'start' | 'end', date?: Date | null): void => {
    if (!date) return;

    if (period === 'start') {
      setCustomStartDate(date);
      return;
    }

    setCustomEndDate(date);
  };

  const handleChangeCustomDate = (): void => {
    if (!setCustomDate) return;

    if (!customDates?.start) {
      setCustomDatesError((currentValue) => ({ ...currentValue, start: 'Insert the start date' }));
    }

    if (!customDates?.end) {
      setCustomDatesError((currentValue) => ({ ...currentValue, end: 'Insert the end date' }));
      return;
    }

    setCustomDate(customDates.start, customDates.end);
    toggleCustomDateDialog();
  };

  useEffect(() => {
    if (customDates && customDatesError?.start === '' && customDatesError?.end === '') {
      setCustomDatesError(null);
    }
  }, [customDates, customDatesError?.end, customDatesError?.start]);

  return (
    <ChartDateFilterComponent
      values={filterValues}
      currentFilter={currentFilter}
      disabledFilters={disabledFilters}
      handleChangeFilter={handleChangeFilter}
      hasCustomDate={hasCustomDate}
      isCustomDateDialogOpen={isCustomDateDialogOpen}
      toggleCustomDateDialog={toggleCustomDateDialog}
      handleSetCustomDates={handleSetCustomDates}
      customDates={customDates}
      customDatesError={customDatesError}
      handleChangeCustomDate={handleChangeCustomDate}
    />
  );
};

export default ChartDateFilterContainer;
